import React, { useEffect, useState, useCallback } from 'react';
import { engagementApi, vendorsApi } from '../api/client';

type Tab = 'dashboard' | 'interactions' | 'action-items' | 'messages' | 'calendar';

export default function Engagement() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [analytics, setAnalytics] = useState<any>(null);
  const [engagements, setEngagements] = useState<any[]>([]);
  const [actionItems, setActionItems] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState<any>(null);

  // Vendor search state
  const [vendorSearch, setVendorSearch] = useState('');
  const filteredVendors = vendorSearch.length >= 2
    ? vendors.filter(v => (v.name || '').toLowerCase().includes(vendorSearch.toLowerCase())).slice(0, 30)
    : vendors.slice(0, 50);

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({ sender_name: '', sender_email: '', signature: '' });
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsRes, vendorsRes, settingsRes] = await Promise.all([
        engagementApi.getAnalytics().catch(() => ({ summary: {} })),
        vendorsApi.list({ page_size: '100' }).catch(() => ({ items: [] })),
        engagementApi.getEmailSettings().catch(() => ({ sender_name: '', sender_email: '', signature: '' })),
      ]);
      setAnalytics(analyticsRes);
      setVendors(vendorsRes.items || []);
      setEmailSettings(settingsRes);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  const loadTabData = useCallback(async (tab: Tab) => {
    try {
      if (tab === 'interactions') {
        const res = await engagementApi.listEngagements();
        setEngagements(res.items || []);
      } else if (tab === 'action-items') {
        const res = await engagementApi.listActionItems();
        setActionItems(res.items || []);
      } else if (tab === 'messages') {
        const res = await engagementApi.listMessages();
        setMessages(res.items || []);
      } else if (tab === 'calendar') {
        const res = await engagementApi.listEvents();
        setEvents(res.items || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadTabData(activeTab); }, [activeTab, loadTabData]);

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'interactions', label: 'Interactions', icon: '🤝' },
    { id: 'action-items', label: 'Action Items', icon: '✅' },
    { id: 'messages', label: 'Messages', icon: '💬' },
    { id: 'calendar', label: 'Calendar', icon: '📅' },
  ];

  const formatDate = (ts: number) => ts ? new Date(ts * 1000).toLocaleDateString() : '—';
  const formatDateTime = (ts: number) => ts ? new Date(ts * 1000).toLocaleString() : '—';

  // ─── Form Handlers ──────────────────────────────────────────────────────

  const handleCreateEngagement = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    try {
      await engagementApi.createEngagement({
        vendor_id: form.get('vendor_id'),
        vendor_name: vendors.find(v => v.id === form.get('vendor_id'))?.name || '',
        type: form.get('type'),
        subject: form.get('subject'),
        notes: form.get('notes'),
        outcome: form.get('outcome'),
        date: Math.floor(Date.now() / 1000),
      });
      setMessage('Interaction recorded');
      setShowForm(false);
      loadTabData('interactions');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleCreateActionItem = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const dueStr = form.get('due_date') as string;
    try {
      await engagementApi.createActionItem({
        title: form.get('title'),
        description: form.get('description'),
        vendor_id: form.get('vendor_id'),
        vendor_name: vendors.find(v => v.id === form.get('vendor_id'))?.name || '',
        assigned_to: form.get('assigned_to'),
        priority: form.get('priority'),
        due_date: dueStr ? Math.floor(new Date(dueStr).getTime() / 1000) : 0,
      });
      setMessage('Action item created');
      setShowForm(false);
      loadTabData('action-items');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleCompleteActionItem = async (id: string) => {
    try {
      await engagementApi.updateActionItem(id, { status: 'completed' });
      setActionItems(prev => prev.map(a => a.id === id ? { ...a, status: 'completed' } : a));
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleCreateEvent = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const startStr = form.get('start_date') as string;
    const endStr = form.get('end_date') as string;
    try {
      await engagementApi.createEvent({
        title: form.get('title'),
        description: form.get('description'),
        event_type: form.get('event_type'),
        start_date: startStr ? Math.floor(new Date(startStr).getTime() / 1000) : 0,
        end_date: endStr ? Math.floor(new Date(endStr).getTime() / 1000) : 0,
        location: form.get('location'),
      });
      setMessage('Event created');
      setShowForm(false);
      loadTabData('calendar');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleGenerateMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setAiGenerating(true);
    setError('');
    try {
      const res = await engagementApi.generateAiMessage({
        vendor_id: form.get('vendor_id'),
        vendor_name: vendors.find(v => v.id === form.get('vendor_id'))?.name || form.get('vendor_search') || '',
        message_type: form.get('message_type'),
        purpose: form.get('purpose'),
        tone: form.get('tone') || 'professional',
      });
      setGeneratedMessage({ ...res, recipient_email: form.get('recipient_email') || '' });
    } catch (err: any) { setError(err.message); }
    setAiGenerating(false);
  };

  const handleSendGeneratedMessage = async () => {
    if (!generatedMessage) return;
    try {
      await engagementApi.sendMessage({
        vendor_id: generatedMessage.vendor_id,
        vendor_name: generatedMessage.vendor_name,
        recipient_email: generatedMessage.recipient_email || '',
        subject: generatedMessage.subject,
        body: generatedMessage.body,
        message_type: generatedMessage.message_type,
        ai_generated: true,
      });
      setMessage('Message sent');
      setGeneratedMessage(null);
      setShowForm(false);
      loadTabData('messages');
      loadData();
    } catch (err: any) { setError(err.message); }
  };

  const handleSaveEmailSettings = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setSettingsSaving(true);
    try {
      const newSettings = {
        sender_name: form.get('sender_name') as string,
        sender_email: form.get('sender_email') as string,
        signature: form.get('signature') as string,
      };
      await engagementApi.saveEmailSettings(newSettings);
      setEmailSettings(newSettings);
      setMessage('Email settings saved');
      setShowSettings(false);
    } catch (err: any) { setError(err.message); }
    setSettingsSaving(false);
  };

  // ─── Stat Card ──────────────────────────────────────────────────────────

  const StatCard = ({ label, value, color }: { label: string; value: any; color?: string }) => (
    <div style={{ background: 'white', borderRadius: 8, padding: '16px 20px', border: '1px solid #e5e7eb', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#1f2937', marginTop: 4 }}>{value ?? 0}</div>
    </div>
  );

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };
  const btnPrimary: React.CSSProperties = { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' };

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, margin: 0 }}>Industry Engagement Portal</h2>
        <button onClick={() => { setShowForm(true); setGeneratedMessage(null); }} style={btnPrimary}>
          + New {activeTab === 'interactions' ? 'Interaction' : activeTab === 'action-items' ? 'Action Item' : activeTab === 'messages' ? 'Message' : activeTab === 'calendar' ? 'Event' : 'Interaction'}
        </button>
      </div>

      {message && <div role="status" aria-live="polite" style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#166534' }}>{message}<button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Dismiss message">x</button></div>}
      {error && <div role="alert" style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#991b1b' }}>{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }} aria-label="Dismiss error">x</button></div>}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid #e5e7eb' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setActiveTab(tab.id); setShowForm(false); }}
            style={{ padding: '12px 16px', background: activeTab === tab.id ? 'white' : 'transparent', color: activeTab === tab.id ? '#1f2937' : '#6b7280', border: 'none', borderBottom: activeTab === tab.id ? '2px solid #2563eb' : 'none', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500, cursor: 'pointer' }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* ─── DASHBOARD TAB ───────────────────────────────────────────── */}
      {activeTab === 'dashboard' && analytics && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard label="Engagements (30d)" value={analytics.summary?.engagements_30d} color="#2563eb" />
            <StatCard label="Vendors Engaged (30d)" value={analytics.summary?.vendors_engaged_30d} color="#7c3aed" />
            <StatCard label="Open Action Items" value={analytics.summary?.open_action_items} color="#f59e0b" />
            <StatCard label="Overdue Items" value={analytics.summary?.overdue_action_items} color="#dc2626" />
            <StatCard label="Messages (30d)" value={analytics.summary?.messages_30d} color="#10b981" />
            <StatCard label="Upcoming Events" value={analytics.summary?.upcoming_events} color="#0891b2" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {/* Top Engaged Vendors */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, marginBottom: 12, color: '#374151' }}>Top Engaged Vendors</h3>
              {analytics.top_engaged_vendors?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analytics.top_engaged_vendors.slice(0, 8).map((v: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: 13, color: '#1f2937' }}>{v.vendor_name}</span>
                      <span style={{ fontSize: 12, background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{v.count}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 13, color: '#9ca3af' }}>No engagement data yet</p>}
            </div>

            {/* Upcoming Events */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, marginBottom: 12, color: '#374151' }}>Upcoming Events</h3>
              {analytics.upcoming_events?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analytics.upcoming_events.slice(0, 6).map((ev: any, i: number) => (
                    <div key={i} style={{ padding: '8px 10px', background: '#f9fafb', borderRadius: 6, borderLeft: '3px solid #2563eb' }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{ev.title}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{formatDate(ev.start_date)} - {ev.event_type}</div>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 13, color: '#9ca3af' }}>No upcoming events</p>}
            </div>

            {/* Overdue Action Items */}
            {analytics.overdue_action_items?.length > 0 && (
              <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', gridColumn: 'span 2' }}>
                <h3 style={{ fontSize: 14, marginBottom: 12, color: '#dc2626' }}>Overdue Action Items</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analytics.overdue_action_items.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#fef2f2', borderRadius: 6 }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{item.vendor_name} - Due: {formatDate(item.due_date)}</div>
                      </div>
                      <span style={{ fontSize: 11, background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: 3, fontWeight: 600 }}>{item.priority}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Engagement by Type */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, marginBottom: 12, color: '#374151' }}>Engagement by Type</h3>
              {Object.keys(analytics.engagement_by_type || {}).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(analytics.engagement_by_type).map(([type, count]: [string, any]) => (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span style={{ fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#2563eb' }}>{count}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 13, color: '#9ca3af' }}>No data yet</p>}
            </div>

            {/* Messages by Type */}
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: 14, marginBottom: 12, color: '#374151' }}>Messages by Type</h3>
              {Object.keys(analytics.messages_by_type || {}).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(analytics.messages_by_type).map(([type, count]: [string, any]) => (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                      <span style={{ fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{type.replace(/_/g, ' ')}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#10b981' }}>{count}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ fontSize: 13, color: '#9ca3af' }}>No messages yet</p>}
            </div>
          </div>
        </div>
      )}

      {/* ─── INTERACTIONS TAB ─────────────────────────────────────────── */}
      {activeTab === 'interactions' && (
        <div>
          {showForm && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Record Vendor Interaction</h3>
              <form onSubmit={handleCreateEngagement} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Vendor (search by name)</label>
                  <input type="text" placeholder="Type to filter..." value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} />
                  <select name="vendor_id" style={inputStyle} required><option value="">Select vendor...</option>{filteredVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
                </div>
                <div><label style={labelStyle}>Type</label><select name="type" style={inputStyle}><option value="meeting">Meeting</option><option value="call">Call</option><option value="email">Email</option><option value="event">Event</option><option value="demo">Demo</option><option value="site_visit">Site Visit</option></select></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Subject</label><input name="subject" style={inputStyle} placeholder="Meeting subject or topic" required /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Notes</label><textarea name="notes" style={{ ...inputStyle, minHeight: 80 }} placeholder="Discussion notes, key points..." /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Outcome / Next Steps</label><input name="outcome" style={inputStyle} placeholder="What was agreed, what's next?" /></div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <button type="submit" style={btnPrimary}>Save Interaction</button>
                  <button type="button" onClick={() => setShowForm(false)} style={{ ...btnPrimary, background: '#6b7280' }}>Cancel</button>
                </div>
              </form>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>{engagements.length} interactions recorded</div>
            {engagements.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No interactions yet. Record your first vendor engagement above.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {engagements.map((eng: any) => (
                  <div key={eng.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                      {eng.type === 'meeting' ? '🤝' : eng.type === 'call' ? '📞' : eng.type === 'email' ? '✉️' : eng.type === 'demo' ? '💻' : '📋'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{eng.subject || 'Untitled'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{eng.vendor_name} - {eng.type} - {formatDate(eng.date || eng.created_at)}</div>
                      {eng.notes && <div style={{ fontSize: 12, color: '#374151', marginTop: 4, lineHeight: 1.4 }}>{eng.notes.substring(0, 150)}{eng.notes.length > 150 ? '...' : ''}</div>}
                      {eng.outcome && <div style={{ fontSize: 11, color: '#059669', marginTop: 4 }}>Outcome: {eng.outcome}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── ACTION ITEMS TAB ──────────────────────────────────────────── */}
      {activeTab === 'action-items' && (
        <div>
          {showForm && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>New Action Item</h3>
              <form onSubmit={handleCreateActionItem} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Title</label><input name="title" style={inputStyle} placeholder="Action item title" required /></div>
                <div>
                  <label style={labelStyle}>Vendor (search by name)</label>
                  <input type="text" placeholder="Type to filter..." value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} />
                  <select name="vendor_id" style={inputStyle}><option value="">No vendor</option>{filteredVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
                </div>
                <div><label style={labelStyle}>Assigned To</label><input name="assigned_to" style={inputStyle} placeholder="Person responsible" /></div>
                <div><label style={labelStyle}>Priority</label><select name="priority" style={inputStyle}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option></select></div>
                <div><label style={labelStyle}>Due Date</label><input name="due_date" type="date" style={inputStyle} /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Description</label><textarea name="description" style={{ ...inputStyle, minHeight: 60 }} placeholder="Details..." /></div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <button type="submit" style={btnPrimary}>Create Item</button>
                  <button type="button" onClick={() => setShowForm(false)} style={{ ...btnPrimary, background: '#6b7280' }}>Cancel</button>
                </div>
              </form>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>
              {actionItems.filter(a => a.status !== 'completed').length} open / {actionItems.length} total
            </div>
            {actionItems.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No action items. Create one to track follow-ups.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {actionItems.map((item: any) => {
                  const isOverdue = item.due_date && item.due_date < Date.now() / 1000 && item.status !== 'completed';
                  const priorityColor = item.priority === 'urgent' ? '#dc2626' : item.priority === 'high' ? '#f59e0b' : item.priority === 'medium' ? '#2563eb' : '#6b7280';
                  return (
                    <div key={item.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 12, alignItems: 'center', opacity: item.status === 'completed' ? 0.6 : 1 }}>
                      <button onClick={() => item.status !== 'completed' && handleCompleteActionItem(item.id)}
                        style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${item.status === 'completed' ? '#10b981' : '#d1d5db'}`, background: item.status === 'completed' ? '#10b981' : 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white', flexShrink: 0 }}>
                        {item.status === 'completed' ? '✓' : ''}
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', textDecoration: item.status === 'completed' ? 'line-through' : 'none' }}>{item.title}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                          {item.vendor_name && <span>{item.vendor_name} - </span>}
                          {item.assigned_to && <span>Assigned: {item.assigned_to} - </span>}
                          {item.due_date > 0 && <span style={{ color: isOverdue ? '#dc2626' : '#6b7280' }}>Due: {formatDate(item.due_date)}</span>}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, background: `${priorityColor}15`, color: priorityColor, padding: '2px 8px', borderRadius: 3, fontWeight: 600, textTransform: 'uppercase' }}>{item.priority}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── MESSAGES TAB ──────────────────────────────────────────────── */}
      {activeTab === 'messages' && (
        <div>
          {showForm && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>Generate AI Industry Message</h3>
              <form onSubmit={handleGenerateMessage} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Vendor (search by name)</label>
                  <input type="text" placeholder="Type to search vendors..." value={vendorSearch} onChange={e => setVendorSearch(e.target.value)} style={{ ...inputStyle, marginBottom: 4 }} />
                  <select name="vendor_id" style={inputStyle} required>
                    <option value="">Select vendor...</option>
                    {filteredVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  {vendorSearch && filteredVendors.length === 0 && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>No vendors match "{vendorSearch}"</div>}
                </div>
                <div><label style={labelStyle}>Recipient Email</label><input name="recipient_email" type="email" style={inputStyle} placeholder="vendor.contact@company.com" /></div>
                <div><label style={labelStyle}>Message Type</label><select name="message_type" style={inputStyle}><option value="outreach">Outreach</option><option value="follow_up">Follow-up</option><option value="rfi">RFI</option><option value="event_invite">Event Invite</option><option value="general">General</option></select></div>
                <div><label style={labelStyle}>Tone</label><select name="tone" style={inputStyle}><option value="professional">Professional</option><option value="casual">Casual</option><option value="formal">Formal</option></select></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Purpose</label><input name="purpose" style={inputStyle} placeholder="e.g. Schedule capability briefing, Request demo, Industry day invite" /></div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={aiGenerating} style={{ ...btnPrimary, background: aiGenerating ? '#93c5fd' : '#7c3aed' }}>
                    {aiGenerating ? '⏳ Generating...' : '🤖 Generate with AI'}
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); setGeneratedMessage(null); setVendorSearch(''); }} style={{ ...btnPrimary, background: '#6b7280' }}>Cancel</button>
                </div>
              </form>

              {generatedMessage && (
                <div style={{ marginTop: 16, background: '#f0f9ff', borderRadius: 8, padding: 16, border: '1px solid #bae6fd' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0c4a6e', textTransform: 'uppercase', marginBottom: 8 }}>AI Generated Message</div>
                  {generatedMessage.recipient_email && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>To: {generatedMessage.recipient_email}</div>}
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Subject: {generatedMessage.subject}</div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{generatedMessage.body}</div>
                  {generatedMessage.talking_points?.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>Talking Points:</div>
                      <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#374151' }}>
                        {generatedMessage.talking_points.map((p: string, i: number) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                  )}
                  {generatedMessage.suggested_follow_up && (
                    <div style={{ fontSize: 12, color: '#059669' }}>Follow-up: {generatedMessage.suggested_follow_up}</div>
                  )}
                  <button onClick={handleSendGeneratedMessage} style={{ ...btnPrimary, marginTop: 12 }}>Send This Message</button>
                </div>
              )}
            </div>
          )}
          {/* Email Settings Panel */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Sending as: <strong>{emailSettings.sender_name || 'Not configured'}</strong>
              {emailSettings.sender_email && <span> ({emailSettings.sender_email})</span>}
            </div>
            <button onClick={() => setShowSettings(!showSettings)} style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
              ⚙️ Email Settings
            </button>
          </div>
          {showSettings && (
            <div style={{ background: '#fafafa', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h4 style={{ fontSize: 13, marginBottom: 12, color: '#374151' }}>Email Sender Settings</h4>
              <form onSubmit={handleSaveEmailSettings} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={labelStyle}>Sender Name</label><input name="sender_name" defaultValue={emailSettings.sender_name} style={inputStyle} placeholder="e.g. Jordan Yarde" /></div>
                <div><label style={labelStyle}>Sender Email</label><input name="sender_email" type="email" defaultValue={emailSettings.sender_email} style={inputStyle} placeholder="jordan@firstoinvest.com" /></div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={labelStyle}>Email Signature</label>
                  <textarea name="signature" defaultValue={emailSettings.signature} style={{ ...inputStyle, minHeight: 100 }} placeholder={"Jordan Yarde\nFirst to Invest Corp\nGovernment Procurement Intelligence\nPhone: (555) 123-4567"} />
                </div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={settingsSaving} style={{ ...btnPrimary, background: settingsSaving ? '#93c5fd' : '#2563eb', fontSize: 12, padding: '8px 16px' }}>
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                  <button type="button" onClick={() => setShowSettings(false)} style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Close</button>
                </div>
              </form>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>{messages.length} messages</div>
            {messages.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No messages yet. Generate your first AI-powered industry message.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {messages.map((msg: any) => (
                  <div key={msg.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{msg.subject || 'No subject'}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          To: {msg.vendor_name}
                          {msg.recipient_email && <span> ({msg.recipient_email})</span>}
                          {' '}- {msg.message_type} - {formatDateTime(msg.created_at)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {msg.ai_generated && <span style={{ fontSize: 10, background: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: 3 }}>AI</span>}
                        <span style={{ fontSize: 10, background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: 3 }}>{msg.status}</span>
                      </div>
                    </div>
                    {msg.body && <div style={{ fontSize: 12, color: '#374151', marginTop: 6, lineHeight: 1.5 }}>{msg.body.substring(0, 200)}{msg.body.length > 200 ? '...' : ''}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── CALENDAR TAB ──────────────────────────────────────────────── */}
      {activeTab === 'calendar' && (
        <div>
          {showForm && (
            <div style={{ background: 'white', borderRadius: 8, padding: 20, border: '1px solid #e5e7eb', marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, marginBottom: 16, color: '#374151' }}>New Event</h3>
              <form onSubmit={handleCreateEvent} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Title</label><input name="title" style={inputStyle} placeholder="Event title" required /></div>
                <div><label style={labelStyle}>Type</label><select name="event_type" style={inputStyle}><option value="meeting">Meeting</option><option value="demo">Demo</option><option value="industry_day">Industry Day</option><option value="conference">Conference</option><option value="deadline">Deadline</option><option value="review">Review</option></select></div>
                <div><label style={labelStyle}>Location</label><input name="location" style={inputStyle} placeholder="Location or virtual link" /></div>
                <div><label style={labelStyle}>Start Date/Time</label><input name="start_date" type="datetime-local" style={inputStyle} required /></div>
                <div><label style={labelStyle}>End Date/Time</label><input name="end_date" type="datetime-local" style={inputStyle} /></div>
                <div style={{ gridColumn: 'span 2' }}><label style={labelStyle}>Description</label><textarea name="description" style={{ ...inputStyle, minHeight: 60 }} placeholder="Event details..." /></div>
                <div style={{ gridColumn: 'span 2', display: 'flex', gap: 8 }}>
                  <button type="submit" style={btnPrimary}>Create Event</button>
                  <button type="button" onClick={() => setShowForm(false)} style={{ ...btnPrimary, background: '#6b7280' }}>Cancel</button>
                </div>
              </form>
            </div>
          )}
          <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', fontSize: 13, color: '#6b7280' }}>{events.length} events</div>
            {events.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No events scheduled. Create your first event above.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {events.map((ev: any) => {
                  const isPast = ev.start_date && ev.start_date < Date.now() / 1000;
                  const typeColors: Record<string, string> = { meeting: '#2563eb', demo: '#7c3aed', industry_day: '#059669', conference: '#0891b2', deadline: '#dc2626', review: '#f59e0b' };
                  return (
                    <div key={ev.id} style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', borderLeft: `4px solid ${typeColors[ev.event_type] || '#6b7280'}`, opacity: isPast ? 0.6 : 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>{ev.title}</div>
                          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                            {formatDateTime(ev.start_date)}
                            {ev.end_date ? ` — ${formatDateTime(ev.end_date)}` : ''}
                            {ev.location && ` | ${ev.location}`}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, background: `${typeColors[ev.event_type] || '#6b7280'}15`, color: typeColors[ev.event_type] || '#6b7280', padding: '2px 8px', borderRadius: 3, fontWeight: 600, textTransform: 'capitalize' }}>{(ev.event_type || '').replace(/_/g, ' ')}</span>
                      </div>
                      {ev.description && <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>{ev.description.substring(0, 150)}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>Loading...</div>}
    </div>
  );
}
